import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  ArrowLeft, CalendarClock, Users as UsersIcon, ShieldCheck, ClipboardCheck,
  Plus, Trash2, Save, Link2, X, ExternalLink, CheckCircle2, CircleDashed,
  CircleDot, Ban, FileCheck2, FlaskConical, FileDown, Video, Edit3, Lightbulb,
  ChevronUp, ChevronDown, Pencil, Paperclip,
} from "lucide-react";
import { formatDate } from "@/lib/format";
import PhotoUploader from "@/components/PhotoUploader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_OPTIONS = ["open", "in_progress", "blocked", "completed"];
const STATUS_COLOR = {
  open: "#1a1a1a",
  in_progress: "#d6a900",
  blocked: "#ff4500",
  completed: "#1a8754",
};
const STATUS_ICON = {
  open: CircleDashed,
  in_progress: CircleDot,
  blocked: Ban,
  completed: CheckCircle2,
};

const SCHEDULES = [
  { k: "once", l: "Once" },
  { k: "monthly", l: "Monthly" },
  { k: "quarterly", l: "Quarterly" },
  { k: "semi_annual", l: "Semi-Annual" },
  { k: "annual", l: "Annual" },
];

export default function VerificationValidationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const isAdmin = ["admin", "controller"].includes(user?.org_role);

  const [item, setItem] = useState(null);
  const [users, setUsers] = useState([]);
  const [hubs, setHubs] = useState([]);
  const [linkedActions, setLinkedActions] = useState([]);
  const [linkOpen, setLinkOpen] = useState(false);
  const [metaEditOpen, setMetaEditOpen] = useState(false);   // iter62 — metadata edit

  const load = async () => {
    const [i, u, h] = await Promise.all([
      api.get(`/vv-items/${id}`),
      api.get("/users/visible-to-me").catch(() => ({ data: [] })),
      api.get("/teams").catch(() => ({ data: [] })),
    ]);
    setItem(i.data);
    setUsers(u.data || []);
    setHubs(h.data || []);
    // Fetch full metadata for linked actions
    const ids = i.data?.linked_action_ids || [];
    if (ids.length) {
      const results = await Promise.all(
        ids.map((aid) => api.get(`/actions/${aid}`).catch(() => null))
      );
      setLinkedActions(results.filter(Boolean).map((r) => r.data));
    } else {
      setLinkedActions([]);
    }
  };
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const uMap = useMemo(() => Object.fromEntries(users.map((x) => [x.id, x])), [users]);
  const hMap = useMemo(() => Object.fromEntries(hubs.map((x) => [x.id, x])), [hubs]);

  if (!item) return (
    <div className="p-10 font-mono text-sm text-[#1a1a1a]/50">Loading…</div>
  );

  const owner = uMap[item.owner_id];
  const hub = hMap[item.hub_id];
  const canEdit = isAdmin ||
    item.owner_id === user?.id ||
    (item.attendee_user_ids || []).includes(user?.id) ||
    item.created_by === user?.id;

  const patchItem = async (patch) => {
    try {
      const r = await api.patch(`/vv-items/${id}`, patch);
      setItem(r.data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const removeItem = async () => {
    if (!confirm("Delete this V&V item permanently? This cannot be undone.")) return;
    await api.delete(`/vv-items/${id}`);
    toast.success("Deleted");
    nav("/app/vv");
  };

  const downloadReport = async () => {
    try {
      const r = await api.get(`/vv-items/${id}/report.pdf`, { responseType: "blob" });
      const sha = r.headers?.["x-content-sha256"] || "";
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `vv-${item.display_id}-report-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(sha ? `PDF downloaded · SHA-256: ${sha.slice(0, 12)}…` : "PDF downloaded");
    } catch (e) {
      toast.error("Report generation failed");
    }
  };

  const unlinkAction = async (aid) => {
    await api.delete(`/vv-items/${id}/link-action/${aid}`);
    toast.success("Unlinked");
    load();
  };

  const Icon = STATUS_ICON[item.status] || CircleDashed;

  return (
    <div className="p-4 lg:p-8 space-y-5" data-testid="vv-detail-page">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Link to="/app/vv" data-testid="vv-back" className="btn-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <div className="flex-1" />
        {canEdit && item.status !== "completed" && (
          <button onClick={() => setMetaEditOpen(true)} data-testid="vv-edit-meta"
            className="btn-pill btn-pill-secondary text-xs">
            <Pencil className="w-3 h-3" /> Edit details
          </button>
        )}
        {canEdit && (
          <select data-testid="vv-status-select" value={item.status || "open"}
            onChange={(e) => patchItem({ status: e.target.value })}
            className="text-[10px] uppercase tracking-wider font-bold px-3 py-1.5 rounded-full border-0"
            style={{ background: `${STATUS_COLOR[item.status] || "#1a1a1a"}18`, color: STATUS_COLOR[item.status] || "#1a1a1a" }}>
            {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
          </select>
        )}
        {isAdmin && (
          <button onClick={removeItem} data-testid="vv-delete"
            className="btn-pill border border-red-300 text-red-600 bg-white hover:bg-red-50">
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
        )}
        <button onClick={downloadReport} data-testid="vv-report"
          className="btn-pill btn-pill-primary text-xs">
          <FileDown className="w-3 h-3" /> Auditor report
        </button>
      </div>

      {/* Hero panel */}
      <section className="surface-card rounded-3xl p-6 lg:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono text-[10px] font-bold text-[#ff4500] uppercase tracking-wider">{item.display_id}</span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full"
                style={{ background: `${STATUS_COLOR[item.status] || "#1a1a1a"}18`, color: STATUS_COLOR[item.status] || "#1a1a1a" }}>
                <Icon className="w-3 h-3" /> {(item.status || "open").replace("_", " ")}
              </span>
              <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1a1a1a]/10">
                {SCHEDULES.find((s) => s.k === item.schedule_type)?.l || item.schedule_type}
              </span>
            </div>
            <h1 className="font-display font-black text-2xl lg:text-3xl leading-tight tracking-tight">{item.title}</h1>
            {item.description && (
              <p className="mt-2 text-sm text-[#1a1a1a]/70 whitespace-pre-wrap">{item.description}</p>
            )}
          </div>
          <div className="flex flex-col gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/70 min-w-[200px]">
            {item.due_date && (
              <div className="inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5 text-[#ff4500]" /> Due {formatDate(item.due_date)}</div>
            )}
            {owner && (
              <div className="inline-flex items-center gap-1.5"><UsersIcon className="w-3.5 h-3.5" /> Owner: <span className="text-[#1a1a1a] font-bold">{owner.name || owner.email}</span></div>
            )}
            {hub && (
              <div className="inline-flex items-center gap-1.5"><UsersIcon className="w-3.5 h-3.5" /> Hub: <span className="text-[#1a1a1a] font-bold">{hub.name}</span></div>
            )}
            {(item.attendee_user_ids || []).length > 0 && (
              <div>Attendees: <span className="text-[#1a1a1a] font-bold">{(item.attendee_user_ids || []).map((uid) => uMap[uid]?.name || "?").join(", ")}</span></div>
            )}
          </div>
        </div>
      </section>

      {/* Two Sections: Verification & Validation — clearly divided */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Section
          testIdPrefix="verification"
          title="Verification"
          subtitle="Ensuring the thing is being done as written procedures describe."
          icon={FileCheck2}
          accent="#1a5fb4"
          item={item}
          canEdit={canEdit}
          section="verification"
          uMap={uMap}
          onReload={load}
          notes={item.verification_notes || ""}
          fileIds={item.verification_file_ids || []}
          onNotesSave={(v) => patchItem({ verification_notes: v })}
          onFilesChange={(ids) => patchItem({ verification_file_ids: ids })}
        />
        <Section
          testIdPrefix="validation"
          title="Validation"
          subtitle="Ensuring what is written is actually working."
          icon={FlaskConical}
          accent="#a13ad6"
          item={item}
          canEdit={canEdit}
          section="validation"
          uMap={uMap}
          onReload={load}
          notes={item.validation_notes || ""}
          fileIds={item.validation_file_ids || []}
          onNotesSave={(v) => patchItem({ validation_notes: v })}
          onFilesChange={(ids) => patchItem({ validation_file_ids: ids })}
        />
        {/* iter53 — Tips section (mirrors Verification/Validation layout) */}
        <Section
          testIdPrefix="tips"
          title="Tips"
          subtitle="Practical guidance, gotchas, best practices for anyone running this V&V."
          icon={Lightbulb}
          accent="#d6a900"
          item={item}
          canEdit={canEdit}
          section="tips"
          uMap={uMap}
          onReload={load}
          notes={item.tips_notes || ""}
          fileIds={item.tips_file_ids || []}
          onNotesSave={(v) => patchItem({ tips_notes: v })}
          onFilesChange={(ids) => patchItem({ tips_file_ids: ids })}
        />
      </div>

      {/* iter57 — Occurrences: stacked history of each scheduled run */}
      {(item.occurrences && item.occurrences.length > 0) && (
        <OccurrencesSection
          item={item}
          canEdit={canEdit}
          onReload={load}
        />
      )}

      {/* Linked Actions — traceability */}
      <section className="surface-card rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Link2 className="w-4 h-4 text-[#ff4500]" />
            <h2 className="font-display font-black text-lg tracking-tight">Linked Actions</h2>
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">{linkedActions.length}</span>
          </div>
          {canEdit && (
            <button onClick={() => setLinkOpen(true)} data-testid="vv-link-action"
              className="btn-pill btn-pill-primary">
              <Plus className="w-3.5 h-3.5" /> Link action
            </button>
          )}
        </div>
        {linkedActions.length === 0 ? (
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/50 py-4 text-center">
            No linked actions yet
          </div>
        ) : (
          <div className="space-y-2">
            {linkedActions.map((a) => (
              <div key={a.id} data-testid={`vv-linked-action-${a.id}`}
                className="flex items-center justify-between p-3 rounded-lg bg-[#f7f5f2] border border-[#1a1a1a]/10">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] font-bold text-[#ff4500] uppercase tracking-wider">{a.display_id}</span>
                    <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-white text-[#1a1a1a]/70">{(a.status || "open").replace("_", " ")}</span>
                  </div>
                  <Link to={`/app/actions/${a.id}`} className="font-display font-bold text-sm text-[#1a1a1a] hover:text-[#ff4500] inline-flex items-center gap-1 mt-0.5">
                    {a.title} <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
                {canEdit && (
                  <button onClick={() => unlinkAction(a.id)} data-testid={`vv-unlink-${a.id}`}
                    className="p-1.5 rounded-lg hover:bg-red-50 text-red-500">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      <LinkActionDialog
        open={linkOpen}
        onClose={() => setLinkOpen(false)}
        vvId={id}
        onLinked={load}
        alreadyLinked={item.linked_action_ids || []}
      />

      {/* iter62 — Metadata edit dialog */}
      <EditVVMetadataDialog
        open={metaEditOpen}
        onClose={() => setMetaEditOpen(false)}
        item={item}
        users={users}
        hubs={hubs}
        onSaved={load}
      />
    </div>
  );
}

function Section({
  testIdPrefix, title, subtitle, icon: Icon, accent,
  item, canEdit, section, uMap, onReload,
  notes, fileIds, onNotesSave, onFilesChange,
}) {
  // iter61 — Nested hierarchy: show all rows in this section with indentation by parent chain.
  const allSubs = (item.sub_tasks || []).filter((s) => s.section === section);
  const depthOf = (s) => {
    let d = 0;
    let cur = s;
    while (cur?.parent_id) {
      cur = allSubs.find((x) => x.id === cur.parent_id);
      d += 1;
      if (d > 8) break;
    }
    return d;
  };
  // Sort so parents render before children (by code lexicographically works for V.1, V.1.1, V.1.1.1)
  const subs = [...allSubs].sort((a, b) => (a.code || "").localeCompare(b.code || "", undefined, { numeric: true }));
  // iter62 — compute sibling boundaries so we know if a subtask is first/last among siblings
  const siblingsByParent = useMemo(() => {
    const map = {};
    for (const s of subs) {
      const key = s.parent_id || "__root__";
      (map[key] = map[key] || []).push(s.id);
    }
    return map;
  }, [subs]);
  const [newTitle, setNewTitle] = useState("");
  const [addingChildOf, setAddingChildOf] = useState(null);   // parent id when adding a nested subtask
  const [childTitle, setChildTitle] = useState("");
  const [notesDraft, setNotesDraft] = useState(notes);
  const [notesDirty, setNotesDirty] = useState(false);
  const [editingId, setEditingId] = useState(null);          // iter53
  const [editDraft, setEditDraft] = useState({ title: "", description: "" });
  const [detailsSubId, setDetailsSubId] = useState(null);    // iter62 — links & notes drawer

  useEffect(() => { setNotesDraft(notes); setNotesDirty(false); }, [notes]);

  const addSub = async () => {
    if (!newTitle.trim()) return;
    try {
      await api.post(`/vv-items/${item.id}/sub-tasks`, { title: newTitle, section });
      toast.success("Sub-task added");
      setNewTitle("");
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };
  const addChild = async (parentId) => {
    if (!childTitle.trim()) return;
    try {
      await api.post(`/vv-items/${item.id}/sub-tasks`, {
        title: childTitle, section, parent_id: parentId,
      });
      setChildTitle(""); setAddingChildOf(null);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const patchSub = async (stid, patch) => {
    try {
      await api.patch(`/vv-items/${item.id}/sub-tasks/${stid}`, patch);
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const deleteSub = async (stid) => {
    if (!confirm("Delete this sub-task?")) return;
    await api.delete(`/vv-items/${item.id}/sub-tasks/${stid}`);
    onReload();
  };

  // iter62 — move subtask up/down within its siblings
  const moveSub = async (stid, direction) => {
    try {
      await api.post(`/vv-items/${item.id}/sub-tasks/${stid}/move`, { direction });
      onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  return (
    <section className="surface-card rounded-2xl p-5 relative overflow-hidden"
      data-testid={`vv-section-${testIdPrefix}`}
      style={{ borderTop: `4px solid ${accent}` }}>
      <div className="flex items-center gap-2 mb-1">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${accent}18`, color: accent }}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="font-display font-black text-lg tracking-tight">{title}</h2>
      </div>
      <p className="text-xs text-[#1a1a1a]/60 mb-4">{subtitle}</p>

      {/* Sub-tasks */}
      <div className="space-y-2 mb-4">
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/70 font-bold">Sub-tasks</div>
        {subs.length === 0 && (
          <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/40 py-3 text-center">
            None yet
          </div>
        )}
        {subs.map((s) => {
          const StatusIcon = STATUS_ICON[s.status] || CircleDashed;
          const depth = depthOf(s);
          return (
            <React.Fragment key={s.id}>
            <div data-testid={`vv-sub-${s.id}`}
              className="flex items-start gap-2 p-2 rounded-lg bg-white border border-[#1a1a1a]/10"
              style={{ marginLeft: `${depth * 22}px` }}>
              {canEdit ? (
                <button onClick={() => patchSub(s.id, { status: s.status === "completed" ? "open" : "completed" })}
                  data-testid={`vv-sub-toggle-${s.id}`}
                  className="mt-0.5 shrink-0" title={s.status}>
                  <StatusIcon className="w-4 h-4" style={{ color: STATUS_COLOR[s.status] || "#1a1a1a" }} />
                </button>
              ) : (
                <StatusIcon className="w-4 h-4 mt-0.5 shrink-0" style={{ color: STATUS_COLOR[s.status] || "#1a1a1a" }} />
              )}
              {/* iter61 — hierarchical code (V.1.1.1) */}
              {s.code && (
                <span className="font-mono font-bold text-[10px] text-[#1a1a1a]/60 mt-1 shrink-0 min-w-[52px]" title="Hierarchical code">
                  {s.code}
                </span>
              )}
              <div className="flex-1 min-w-0">
                {editingId === s.id ? (
                  <div className="space-y-2">
                    <input
                      data-testid={`vv-sub-edit-title-${s.id}`}
                      autoFocus
                      value={editDraft.title}
                      onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                      className="w-full text-sm border border-[#1a1a1a]/20 rounded px-2 py-1"
                      placeholder="Sub-task title"
                    />
                    <textarea
                      data-testid={`vv-sub-edit-desc-${s.id}`}
                      value={editDraft.description}
                      onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                      rows={2}
                      className="w-full text-[11px] border border-[#1a1a1a]/20 rounded px-2 py-1"
                      placeholder="Optional description / notes"
                    />
                    <div className="flex gap-2">
                      <button
                        data-testid={`vv-sub-edit-save-${s.id}`}
                        onClick={async () => {
                          if (!editDraft.title.trim()) return toast.error("Title required");
                          await patchSub(s.id, { title: editDraft.title.trim(), description: editDraft.description });
                          setEditingId(null);
                        }}
                        className="text-[10px] uppercase tracking-wider font-bold bg-[#1a1a1a] text-white px-2 py-1 rounded"
                      >Save</button>
                      <button
                        onClick={() => setEditingId(null)}
                        className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/60"
                      >Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className={`text-sm ${s.status === "completed" ? "line-through text-[#1a1a1a]/50" : "text-[#1a1a1a]"}`}>{s.title}</div>
                    {s.description && <div className="text-[11px] text-[#1a1a1a]/50 mt-0.5">{s.description}</div>}
                    <div className="mt-1 flex items-center gap-2 text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/50">
                      {s.owner_id && <span>Owner: {uMap[s.owner_id]?.name || "?"}</span>}
                      {s.due_date && <span>Due {formatDate(s.due_date)}</span>}
                    </div>
                  </>
                )}
              </div>
              {canEdit && editingId !== s.id && (
                <>
                  {(() => {
                    const siblings = siblingsByParent[s.parent_id || "__root__"] || [];
                    const posInSiblings = siblings.indexOf(s.id);
                    const isFirst = posInSiblings === 0;
                    const isLast = posInSiblings === siblings.length - 1;
                    return (
                      <>
                        <button
                          onClick={() => moveSub(s.id, "up")}
                          disabled={isFirst}
                          data-testid={`vv-sub-move-up-${s.id}`}
                          className="p-1 rounded hover:bg-[#1a1a1a]/06 text-[#1a1a1a]/60 disabled:opacity-25 disabled:cursor-not-allowed"
                          title="Move up"
                        >
                          <ChevronUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => moveSub(s.id, "down")}
                          disabled={isLast}
                          data-testid={`vv-sub-move-down-${s.id}`}
                          className="p-1 rounded hover:bg-[#1a1a1a]/06 text-[#1a1a1a]/60 disabled:opacity-25 disabled:cursor-not-allowed"
                          title="Move down"
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </>
                    );
                  })()}
                  <button
                    onClick={() => setDetailsSubId(s.id)}
                    data-testid={`vv-sub-details-${s.id}`}
                    className="p-1 rounded hover:bg-[#1a1a1a]/06 text-[#1a1a1a]/60 relative"
                    title="Files, links & notes"
                  >
                    <Link2 className="w-3.5 h-3.5" />
                    {((s.file_ids?.length || 0) + (s.linked_action_ids?.length || 0)
                      + (s.linked_vv_ids?.length || 0) + (s.linked_meeting_ids?.length || 0)) > 0 && (
                      <span className="absolute -top-1 -right-1 bg-[#ff4500] text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] px-1 flex items-center justify-center">
                        {(s.file_ids?.length || 0) + (s.linked_action_ids?.length || 0)
                          + (s.linked_vv_ids?.length || 0) + (s.linked_meeting_ids?.length || 0)}
                      </span>
                    )}
                  </button>
                  <button
                    onClick={() => setAddingChildOf(addingChildOf === s.id ? null : s.id)}
                    data-testid={`vv-sub-add-child-${s.id}`}
                    className="p-1 rounded hover:bg-[#1a1a1a]/06 text-[#1a1a1a]/60"
                    title="Add nested sub-task"
                  >
                    <Plus className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => { setEditingId(s.id); setEditDraft({ title: s.title, description: s.description || "" }); }}
                    data-testid={`vv-sub-edit-${s.id}`}
                    className="p-1 rounded hover:bg-[#1a1a1a]/06 text-[#1a1a1a]/60"
                    title="Edit sub-task"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => deleteSub(s.id)} data-testid={`vv-sub-delete-${s.id}`}
                    className="p-1 rounded hover:bg-red-50 text-red-500">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </>
              )}
            </div>
            {addingChildOf === s.id && canEdit && (
              <div className="flex gap-1.5" style={{ marginLeft: `${(depth + 1) * 22}px` }}>
                <input
                  autoFocus
                  data-testid={`vv-sub-child-input-${s.id}`}
                  value={childTitle}
                  onChange={(e) => setChildTitle(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addChild(s.id)}
                  placeholder={`Nested subtask under ${s.code}…`}
                  className="flex-1 text-[12px] border border-[#1a1a1a]/15 rounded px-2 py-1"
                />
                <button onClick={() => addChild(s.id)}
                  data-testid={`vv-sub-child-add-${s.id}`}
                  disabled={!childTitle.trim()}
                  className="btn-pill btn-pill-primary text-[10px] disabled:opacity-40">
                  + Nest
                </button>
                <button onClick={() => { setAddingChildOf(null); setChildTitle(""); }}
                  className="btn-pill btn-pill-secondary text-[10px]">Cancel</button>
              </div>
            )}
          </React.Fragment>
          );
        })}
        {canEdit && (
          <div className="flex gap-2">
            <input
              data-testid={`vv-sub-add-input-${testIdPrefix}`}
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSub()}
              placeholder="Add sub-task…"
              className="flex-1 text-sm border border-[#1a1a1a]/20 rounded-lg px-3 py-1.5" />
            <button onClick={addSub} data-testid={`vv-sub-add-${testIdPrefix}`}
              className="btn-pill btn-pill-primary text-xs">
              <Plus className="w-3 h-3" /> Add
            </button>
          </div>
        )}
      </div>

      {/* Notes */}
      <div className="space-y-2 mb-4">
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/70 font-bold">Notes / Evidence</div>
        <textarea
          data-testid={`vv-notes-${testIdPrefix}`}
          value={notesDraft}
          disabled={!canEdit}
          rows={4}
          onChange={(e) => { setNotesDraft(e.target.value); setNotesDirty(e.target.value !== notes); }}
          placeholder={section === "verification" ? "Document what evidence proves the program is being followed as written…" : "Document what evidence proves the written program actually works…"}
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm disabled:bg-[#f7f5f2]" />
        {canEdit && notesDirty && (
          <button onClick={() => onNotesSave(notesDraft)}
            data-testid={`vv-notes-save-${testIdPrefix}`}
            className="btn-pill btn-pill-primary text-xs">
            <Save className="w-3 h-3" /> Save notes
          </button>
        )}
      </div>

      {/* Attachments */}
      <div>
        <div className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/70 font-bold mb-2">Attachments / Evidence Files</div>
        {canEdit ? (
          <PhotoUploader photoIds={fileIds} onChange={onFilesChange} testIdPrefix={`vv-files-${testIdPrefix}`} />
        ) : (
          <div className="text-[11px] text-[#1a1a1a]/40 font-mono uppercase tracking-wider">{fileIds.length} file(s)</div>
        )}
      </div>

      {/* iter62 — Subtask details drawer (links, files, notes) */}
      <SubTaskDetailsDrawer
        open={!!detailsSubId}
        subtask={subs.find((x) => x.id === detailsSubId) || null}
        vvId={item.id}
        canEdit={canEdit}
        onClose={() => setDetailsSubId(null)}
        onSaved={onReload}
      />
    </section>
  );
}

function LinkActionDialog({ open, onClose, vvId, onLinked, alreadyLinked }) {
  const [actions, setActions] = useState([]);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/actions", { params: { include_tasks: false } }).then((r) => {
      // Filter out V&V rows (they're not actual actions) and already-linked
      const linked = new Set(alreadyLinked || []);
      setActions((r.data || []).filter((a) => a.kind !== "vv" && a.kind !== "task" && !linked.has(a.id)));
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const linkOne = async (aid) => {
    try {
      await api.post(`/vv-items/${vvId}/link-action`, { action_id: aid });
      toast.success("Linked");
      onLinked?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const shown = actions.filter((a) => !q || (a.title || "").toLowerCase().includes(q.toLowerCase()) || (a.display_id || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight">Link an Action</DialogTitle>
        </DialogHeader>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="vv-link-search"
          placeholder="Search actions…"
          className="w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
        <div className="max-h-96 overflow-auto space-y-1.5 mt-2">
          {shown.length === 0 && <div className="text-[11px] font-mono uppercase tracking-wider text-[#1a1a1a]/40 py-6 text-center">No matching actions</div>}
          {shown.map((a) => (
            <button key={a.id} data-testid={`vv-link-choose-${a.id}`} onClick={() => linkOne(a.id)}
              className="w-full text-left p-2.5 rounded-lg border border-[#1a1a1a]/10 hover:border-[#ff4500] hover:bg-[#fff1ea] transition-colors">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[10px] font-bold text-[#ff4500] uppercase tracking-wider">{a.display_id}</span>
                <span className="text-[9px] uppercase tracking-wider text-[#1a1a1a]/60">{(a.status || "").replace("_", " ")}</span>
              </div>
              <div className="text-sm font-display font-bold mt-0.5">{a.title}</div>
            </button>
          ))}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="btn-pill btn-pill-ghost" data-testid="vv-link-cancel">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// iter57 — Occurrences panel: master card with a stacked box per due date.
function OccurrencesSection({ item, canEdit, onReload }) {
  const [expandedId, setExpandedId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [addDate, setAddDate] = useState("");
  const [meetings, setMeetings] = useState([]);
  const [signOccId, setSignOccId] = useState(null);   // which occ is being signed
  const [signName, setSignName] = useState("");
  const [signMeetingId, setSignMeetingId] = useState("");

  const occs = [...(item.occurrences || [])].sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));
  const openOne = occs.find((o) => o.status !== "completed");
  useEffect(() => { if (openOne && !expandedId) setExpandedId(openOne.id); }, [openOne?.id]);   // eslint-disable-line

  // Load recent meetings once for the "link to meeting" picker.
  useEffect(() => {
    api.get("/formal-meetings").then((r) => setMeetings(r.data || [])).catch(() => {});
  }, []);

  const patchOcc = async (occId, patch) => {
    setBusy(true);
    try {
      await api.patch(`/vv-items/${item.id}/occurrences/${occId}`, patch);
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const openSignDialog = (occ) => {
    setSignOccId(occ.id);
    setSignName("");
    // Default to the currently-active meeting (most recent open one), if any.
    const active = meetings.find((m) => m.status === "in_progress" || m.status === "open");
    setSignMeetingId(active?.id || "");
  };
  const closeSignDialog = () => { setSignOccId(null); setSignName(""); setSignMeetingId(""); };

  const submitSignature = async () => {
    if (!signName.trim()) return toast.error("Type your full name to sign");
    const occ = occs.find((o) => o.id === signOccId);
    if (!occ) return;
    setBusy(true);
    try {
      await api.post(`/vv-items/${item.id}/occurrences/${occ.id}/complete`, {
        signature_name: signName.trim(),
        verification_notes: occ.verification_notes || "",
        validation_notes: occ.validation_notes || "",
        tips_notes: occ.tips_notes || "",
        meeting_id: signMeetingId || null,
      });
      toast.success(signMeetingId ? "Signed & linked to meeting" : "Signed & completed");
      closeSignDialog();
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const addOccurrence = async () => {
    if (!addDate) return toast.error("Pick a date");
    setBusy(true);
    try {
      await api.post(`/vv-items/${item.id}/occurrences?due_date=${addDate}`);
      setAddDate("");
      onReload?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const rec = item.recurrence || {};

  return (
    <section className="surface-card rounded-2xl p-5" data-testid="vv-occurrences-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <CalendarClock className="w-4 h-4 text-[#ff4500]" />
          <h2 className="font-display font-black text-lg tracking-tight">Recurrence — {occs.length} occurrences</h2>
          <span className="text-[10px] font-mono uppercase tracking-wider text-[#1a1a1a]/55">
            {rec.type === "rule" ? `${rec.cadence || "custom"} rule` : rec.type === "dates" ? "fixed dates" : "manual"}
          </span>
        </div>
        {canEdit && (
          <div className="flex items-center gap-2">
            <input type="date" value={addDate} onChange={(e) => setAddDate(e.target.value)}
              data-testid="vv-occ-add-date"
              className="border border-[#1a1a1a]/20 rounded-lg px-2 py-1 text-xs" />
            <button onClick={addOccurrence} disabled={busy || !addDate}
              data-testid="vv-occ-add-btn"
              className="btn-pill btn-pill-secondary text-[10px]"><Plus className="w-3 h-3" />Add occurrence</button>
          </div>
        )}
      </div>

      <ol className="space-y-2">
        {occs.map((occ, i) => {
          const isCompleted = occ.status === "completed";
          const isExpanded = expandedId === occ.id;
          return (
            <li key={occ.id} data-testid={`vv-occ-${occ.id}`}
              className={`rounded-xl border ${isCompleted ? "border-[#1a8754]/25 bg-[#e8f5ee]/40" : "border-[#ff4500]/25 bg-white"}`}>
              <button onClick={() => setExpandedId(isExpanded ? null : occ.id)}
                data-testid={`vv-occ-toggle-${occ.id}`}
                className="w-full flex items-center justify-between px-3 py-2 text-left">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono font-bold text-[11px] text-[#1a1a1a]/60">#{i + 1}</span>
                  <span className="font-display font-bold text-sm">{formatDate(occ.due_date)}</span>
                  {isCompleted ? (
                    <>
                      <span className="inline-block bg-[#1a8754] text-white font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full">Signed</span>
                      <span className="text-[10px] text-[#1a1a1a]/60">by {occ.completed_by_name} · {formatDate(occ.completed_at)}</span>
                    </>
                  ) : (
                    <span className="inline-block bg-[#ff4500] text-white font-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full">Open</span>
                  )}
                </div>
                <span className="text-[11px] text-[#1a1a1a]/40">{isExpanded ? "hide" : "open"}</span>
              </button>
              {isExpanded && (
                <div className="border-t border-[#1a1a1a]/06 p-3 space-y-2 text-[12px]">
                  {isCompleted ? (
                    <>
                      <div><b>Verification:</b> {occ.verification_notes || <span className="italic text-[#1a1a1a]/40">—</span>}</div>
                      <div><b>Validation:</b> {occ.validation_notes || <span className="italic text-[#1a1a1a]/40">—</span>}</div>
                      <div><b>Tips:</b> {occ.tips_notes || <span className="italic text-[#1a1a1a]/40">—</span>}</div>
                      {occ.linked_meeting_id && (
                        <div className="pt-1">
                          <Link to={`/app/meetings/${occ.linked_meeting_id}`}
                            data-testid={`vv-occ-meeting-link-${occ.id}`}
                            className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-[#1a5fb4] hover:underline">
                            <Link2 className="w-3 h-3" />
                            Signed in meeting {occ.linked_meeting_display_id || ""} — {occ.linked_meeting_title || "open"}
                            <ExternalLink className="w-3 h-3" />
                          </Link>
                        </div>
                      )}
                      <div className="text-[10px] font-mono text-[#1a1a1a]/50">sig · {(occ.signature_hash || "").slice(0, 16)}…</div>
                    </>
                  ) : (
                    <>
                      <textarea
                        value={occ.verification_notes || ""}
                        onChange={(e) => patchOcc(occ.id, { verification_notes: e.target.value })}
                        placeholder="Verification notes for this occurrence…"
                        className="w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-[12px]" rows={2}
                        data-testid={`vv-occ-verif-${occ.id}`}
                      />
                      <textarea
                        value={occ.validation_notes || ""}
                        onChange={(e) => patchOcc(occ.id, { validation_notes: e.target.value })}
                        placeholder="Validation notes for this occurrence…"
                        className="w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-[12px]" rows={2}
                        data-testid={`vv-occ-valid-${occ.id}`}
                      />
                      {canEdit && (
                        <button onClick={() => openSignDialog(occ)} disabled={busy}
                          data-testid={`vv-occ-complete-${occ.id}`}
                          className="btn-pill btn-pill-primary text-[11px]">
                          <ShieldCheck className="w-3 h-3" /> Sign &amp; complete
                        </button>
                      )}
                    </>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ol>

      {/* iter57 — Sign-off dialog with optional meeting link */}
      <Dialog open={!!signOccId} onOpenChange={(o) => !o && closeSignDialog()}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="vv-sign-dialog">
          <DialogHeader>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold">// Sign V&V occurrence</div>
            <DialogTitle className="font-display font-black text-lg">
              Sign &amp; complete
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-[12px]">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Your full name (signature)</label>
              <input type="text" value={signName} onChange={(e) => setSignName(e.target.value)}
                autoFocus placeholder="e.g. Marcus Chen"
                data-testid="vv-sign-name"
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Link to meeting <span className="normal-case text-[10px] tracking-normal text-[#1a1a1a]/50">(optional — closes the audit trail loop)</span></label>
              <select value={signMeetingId} onChange={(e) => setSignMeetingId(e.target.value)}
                data-testid="vv-sign-meeting"
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— No linked meeting —</option>
                {meetings.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.display_id || m.id.slice(0, 8)} · {m.title} · {m.status}
                  </option>
                ))}
              </select>
              {meetings.length === 0 && (
                <div className="text-[10px] text-[#1a1a1a]/50 mt-1">No meetings in this org yet. You can sign without a link.</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <button onClick={closeSignDialog} data-testid="vv-sign-cancel"
              className="btn-pill btn-pill-secondary text-[11px]">
              Cancel
            </button>
            <button onClick={submitSignature} disabled={busy || !signName.trim()}
              data-testid="vv-sign-submit"
              className="btn-pill btn-pill-primary text-[11px]">
              <ShieldCheck className="w-3 h-3" /> Sign
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}


// iter62 — Edit root V&V metadata (title, description, owner, hub, due date, schedule, attendees)
function EditVVMetadataDialog({ open, onClose, item, users, hubs, onSaved }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setForm({
      title: item.title || "",
      description: item.description || "",
      owner_id: item.owner_id || "",
      hub_id: item.hub_id || "",
      due_date: (item.due_date || "").slice(0, 10),
      schedule_type: item.schedule_type || "once",
      attendee_user_ids: item.attendee_user_ids || [],
    });
  }, [open, item]);

  if (!form) return null;

  const toggleAttendee = (uid) => {
    setForm((f) => {
      const set = new Set(f.attendee_user_ids || []);
      set.has(uid) ? set.delete(uid) : set.add(uid);
      return { ...f, attendee_user_ids: [...set] };
    });
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error("Title required");
    setBusy(true);
    try {
      const patch = {
        title: form.title.trim(),
        description: form.description || "",
        owner_id: form.owner_id || null,
        hub_id: form.hub_id || null,
        due_date: form.due_date || null,
        schedule_type: form.schedule_type,
        attendee_user_ids: form.attendee_user_ids,
      };
      await api.patch(`/vv-items/${item.id}`, patch);
      toast.success("V&V details updated");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="vv-edit-meta-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold">// Edit V&V details</div>
          <DialogTitle className="font-display font-black text-lg">{item.display_id}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1 text-[12px] max-h-[65vh] overflow-auto pr-1">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Title *</label>
            <input data-testid="vv-edit-title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Description / Scope</label>
            <textarea data-testid="vv-edit-desc" value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Owner</label>
              <select data-testid="vv-edit-owner" value={form.owner_id}
                onChange={(e) => setForm({ ...form, owner_id: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— Unassigned —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Hub</label>
              <select data-testid="vv-edit-hub" value={form.hub_id}
                onChange={(e) => setForm({ ...form, hub_id: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="">— None —</option>
                {hubs.map((h) => <option key={h.id} value={h.id}>{h.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Due Date</label>
              <input type="date" data-testid="vv-edit-due" value={form.due_date}
                onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Frequency</label>
              <select data-testid="vv-edit-freq" value={form.schedule_type}
                onChange={(e) => setForm({ ...form, schedule_type: e.target.value })}
                className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm bg-white">
                {SCHEDULES.map((s) => <option key={s.k} value={s.k}>{s.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#1a1a1a]/70">Attendees</label>
            <div className="mt-1 border border-[#1a1a1a]/20 rounded-lg p-2 max-h-40 overflow-auto flex flex-wrap gap-1.5">
              {users.length === 0 && <div className="text-[11px] text-[#1a1a1a]/40">No users visible</div>}
              {users.map((u) => {
                const on = form.attendee_user_ids.includes(u.id);
                return (
                  <button key={u.id} type="button" onClick={() => toggleAttendee(u.id)}
                    data-testid={`vv-edit-attendee-${u.id}`}
                    className={`text-[11px] px-2 py-0.5 rounded-full border ${on ? "bg-[#ff4500] text-white border-[#ff4500]" : "bg-white text-[#1a1a1a] border-[#1a1a1a]/20"}`}>
                    {u.name || u.email}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} data-testid="vv-edit-cancel"
            className="btn-pill btn-pill-secondary text-[11px]">
            <X className="w-3 h-3" /> Cancel
          </button>
          <button onClick={submit} disabled={busy || !form.title.trim()}
            data-testid="vv-edit-save"
            className="btn-pill btn-pill-primary text-[11px]">
            <Save className="w-3 h-3" /> {busy ? "Saving…" : "Save changes"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// iter62 — Subtask details drawer: files, linked actions, linked V&V items, linked meetings, free-text notes
function SubTaskDetailsDrawer({ open, subtask, vvId, canEdit, onClose, onSaved }) {
  const [notesDraft, setNotesDraft] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);
  const [fileIds, setFileIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [actions, setActions] = useState([]);
  const [vvs, setVvs] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(null);   // 'action' | 'vv' | 'meeting' | null
  const [pickerQuery, setPickerQuery] = useState("");

  useEffect(() => {
    if (!open || !subtask) return;
    setNotesDraft(subtask.notes || "");
    setNotesDirty(false);
    setFileIds(subtask.file_ids || []);
    // Load pickable options once
    Promise.all([
      api.get("/actions", { params: { include_tasks: false } }).catch(() => ({ data: [] })),
      api.get("/vv-items").catch(() => ({ data: [] })),
      api.get("/formal-meetings").catch(() => ({ data: [] })),
    ]).then(([a, v, m]) => {
      setActions((a.data || []).filter((x) => x.kind !== "vv" && x.kind !== "task"));
      setVvs((v.data || []).filter((x) => x.id !== vvId));
      setMeetings(m.data || []);
    });
  }, [open, subtask?.id, vvId]);

  if (!open || !subtask) return null;

  const patch = async (body) => {
    setBusy(true);
    try {
      await api.patch(`/vv-items/${vvId}/sub-tasks/${subtask.id}`, body);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const saveNotes = async () => {
    await patch({ notes: notesDraft });
    setNotesDirty(false);
    toast.success("Notes saved");
  };

  const saveFiles = async (ids) => {
    setFileIds(ids);
    await patch({ file_ids: ids });
  };

  const toggleLink = async (kind, id) => {
    const field = { action: "linked_action_ids", vv: "linked_vv_ids", meeting: "linked_meeting_ids" }[kind];
    const current = subtask[field] || [];
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    await patch({ [field]: next });
  };

  const linkedActionsFull = actions.filter((a) => (subtask.linked_action_ids || []).includes(a.id));
  const linkedVvsFull = vvs.filter((v) => (subtask.linked_vv_ids || []).includes(v.id));
  const linkedMeetingsFull = meetings.filter((m) => (subtask.linked_meeting_ids || []).includes(m.id));

  const pickerOptions = pickerOpen === "action" ? actions
    : pickerOpen === "vv" ? vvs
    : pickerOpen === "meeting" ? meetings
    : [];
  const pickerLinked = pickerOpen === "action" ? (subtask.linked_action_ids || [])
    : pickerOpen === "vv" ? (subtask.linked_vv_ids || [])
    : pickerOpen === "meeting" ? (subtask.linked_meeting_ids || [])
    : [];
  const pickerFiltered = pickerOptions.filter((o) => {
    if (!pickerQuery) return true;
    const q = pickerQuery.toLowerCase();
    return (o.title || "").toLowerCase().includes(q) || (o.display_id || "").toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-2xl rounded-2xl" data-testid="vv-sub-details-dialog">
        <DialogHeader>
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#ff4500] font-bold">
            // Sub-task {subtask.code}
          </div>
          <DialogTitle className="font-display font-black text-lg">{subtask.title}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1 text-[12px] max-h-[70vh] overflow-auto pr-1">
          {subtask.description && (
            <div className="text-[11px] text-[#1a1a1a]/60 whitespace-pre-wrap bg-[#faf8f4] rounded-lg p-2">
              {subtask.description}
            </div>
          )}

          {/* Free-text notes */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider font-bold text-[#1a1a1a]/70">Notes</label>
            <textarea
              data-testid="vv-sub-notes"
              value={notesDraft}
              disabled={!canEdit}
              rows={3}
              onChange={(e) => { setNotesDraft(e.target.value); setNotesDirty(e.target.value !== (subtask.notes || "")); }}
              placeholder="Free-form notes / context for this sub-task…"
              className="mt-1 w-full border border-[#1a1a1a]/20 rounded-lg px-3 py-2 text-sm disabled:bg-[#f7f5f2]"
            />
            {canEdit && notesDirty && (
              <button onClick={saveNotes} disabled={busy}
                data-testid="vv-sub-notes-save"
                className="btn-pill btn-pill-primary text-[11px] mt-1.5">
                <Save className="w-3 h-3" /> Save notes
              </button>
            )}
          </div>

          {/* Files */}
          <div>
            <label className="text-[10px] font-mono uppercase tracking-wider font-bold text-[#1a1a1a]/70 flex items-center gap-1">
              <Paperclip className="w-3 h-3" /> Files / evidence
            </label>
            {canEdit ? (
              <div className="mt-1"><PhotoUploader photoIds={fileIds} onChange={saveFiles} testIdPrefix={`vv-sub-${subtask.id}-files`} /></div>
            ) : (
              <div className="mt-1 text-[11px] text-[#1a1a1a]/50">{fileIds.length} file(s)</div>
            )}
          </div>

          {/* Linked Actions */}
          <LinkGroup
            label="Linked Actions"
            testId="vv-sub-links-actions"
            color="#ff4500"
            items={linkedActionsFull}
            linkTo={(a) => `/app/actions/${a.id}`}
            canEdit={canEdit}
            onAdd={() => { setPickerOpen("action"); setPickerQuery(""); }}
            onRemove={(a) => toggleLink("action", a.id)}
          />

          {/* Linked V&V */}
          <LinkGroup
            label="Linked V&V items"
            testId="vv-sub-links-vv"
            color="#a13ad6"
            items={linkedVvsFull}
            linkTo={(v) => `/app/vv/${v.id}`}
            canEdit={canEdit}
            onAdd={() => { setPickerOpen("vv"); setPickerQuery(""); }}
            onRemove={(v) => toggleLink("vv", v.id)}
          />

          {/* Linked Meetings */}
          <LinkGroup
            label="Linked Meetings"
            testId="vv-sub-links-meetings"
            color="#1a5fb4"
            items={linkedMeetingsFull}
            linkTo={(m) => `/app/meetings/${m.id}`}
            canEdit={canEdit}
            onAdd={() => { setPickerOpen("meeting"); setPickerQuery(""); }}
            onRemove={(m) => toggleLink("meeting", m.id)}
          />

          {/* Picker */}
          {pickerOpen && (
            <div className="border-2 border-[#1a1a1a] rounded-xl p-3 bg-white" data-testid="vv-sub-picker">
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-[10px] uppercase tracking-wider font-bold">
                  Pick {pickerOpen === "action" ? "Actions" : pickerOpen === "vv" ? "V&V items" : "Meetings"} to link
                </div>
                <button onClick={() => setPickerOpen(null)} className="text-[#1a1a1a]/50 hover:text-[#ff4500]">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <input value={pickerQuery} onChange={(e) => setPickerQuery(e.target.value)}
                data-testid="vv-sub-picker-search"
                placeholder="Search…"
                className="w-full border border-[#1a1a1a]/20 rounded-lg px-2 py-1.5 text-[12px] mb-2" />
              <div className="max-h-64 overflow-auto space-y-1">
                {pickerFiltered.length === 0 && <div className="text-[11px] text-[#1a1a1a]/40 py-3 text-center">No matches</div>}
                {pickerFiltered.map((o) => {
                  const linked = pickerLinked.includes(o.id);
                  return (
                    <button key={o.id}
                      data-testid={`vv-sub-picker-choose-${o.id}`}
                      onClick={() => toggleLink(pickerOpen, o.id)}
                      className={`w-full text-left p-2 rounded-lg border transition-colors ${
                        linked ? "border-[#ff4500] bg-[#fff1ea]" : "border-[#1a1a1a]/10 hover:border-[#1a1a1a]/30"
                      }`}>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-[10px] font-bold text-[#ff4500]">{o.display_id}</span>
                        <span className="text-[10px] uppercase text-[#1a1a1a]/50">{o.status || ""}</span>
                        {linked && <span className="text-[10px] text-[#1a8754] font-bold ml-auto">✓ linked</span>}
                      </div>
                      <div className="text-[12px] font-bold mt-0.5 truncate">{o.title}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <button onClick={onClose} data-testid="vv-sub-details-close"
            className="btn-pill btn-pill-secondary text-[11px]">
            <X className="w-3 h-3" /> Close
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LinkGroup({ label, testId, color, items, linkTo, canEdit, onAdd, onRemove }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[10px] font-mono uppercase tracking-wider font-bold text-[#1a1a1a]/70">
          {label} <span className="text-[#1a1a1a]/40">({items.length})</span>
        </label>
        {canEdit && (
          <button onClick={onAdd} data-testid={`${testId}-add`}
            className="text-[10px] uppercase tracking-wider font-bold text-[#ff4500] hover:underline inline-flex items-center gap-0.5">
            <Plus className="w-3 h-3" /> Link
          </button>
        )}
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-[#1a1a1a]/40 py-2 text-center bg-[#faf8f4] rounded-lg" data-testid={`${testId}-empty`}>
          None linked yet
        </div>
      ) : (
        <div className="space-y-1" data-testid={`${testId}-list`}>
          {items.map((o) => (
            <div key={o.id} className="flex items-center gap-2 p-2 rounded-lg bg-[#faf8f4] border border-[#1a1a1a]/06">
              <div className="w-1.5 h-6 rounded-full" style={{ backgroundColor: color }} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[10px] font-bold" style={{ color }}>{o.display_id}</span>
                  <span className="text-[10px] uppercase text-[#1a1a1a]/50">{o.status || ""}</span>
                </div>
                <Link to={linkTo(o)} className="text-[12px] font-bold text-[#1a1a1a] hover:text-[#ff4500] inline-flex items-center gap-1 truncate">
                  {o.title} <ExternalLink className="w-3 h-3 shrink-0" />
                </Link>
              </div>
              {canEdit && (
                <button onClick={() => onRemove(o)} data-testid={`${testId}-remove-${o.id}`}
                  className="p-1 rounded hover:bg-red-50 text-red-500 shrink-0">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

